// Phase 0089 — Start Study Wizard.
// Big-touch wizard: pick template (if multiple) → pick context (employee + machine + shift)
// → confirm + auto-fetch preview → opens the live capture page.

import { useEffect, useState } from 'react'
import {
  App, Alert, Button, Card, Form, Select, Space, Steps, Tag, Typography,
} from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { liveStudyApi } from '@/features/hr/liveStudyApi'
import { employeesApi } from '@/features/hr/api'
import { departmentsApi } from '@/features/masters/departments/api'
import { machinesApi } from '@/features/masters/machines/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

export default function StartStudyWizardPage() {
  const [params] = useSearchParams()
  const studyId = params.get('study_id') || ''
  // Phase 0090 — task-driven entry: if the user clicked "Start Live Study"
  // from a To-Do, we receive task_id and bind it to the session.
  const taskId = params.get('task_id') || undefined
  const navigate = useNavigate()
  const { message } = App.useApp()
  const { plantId } = usePlantId()

  const [step, setStep] = useState(0)
  const [templateId, setTemplateId] = useState<string | undefined>()
  const [departmentId, setDepartmentId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [employeeId, setEmployeeId] = useState<string | undefined>()

  const { data: templates = [] } = useQuery({
    queryKey: ['study-templates', studyId],
    queryFn: () => liveStudyApi.listTemplates(studyId),
    enabled: !!studyId,
  })

  // Auto-pick the only template if there's exactly one
  useEffect(() => {
    if (templates.length === 1 && !templateId) {
      setTemplateId(templates[0].id)
      setStep(1)
    }
  }, [templates, templateId])

  const { data: depts } = useQuery({
    queryKey: ['depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 100, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: machinesData } = useQuery({
    queryKey: ['machines', plantId, departmentId],
    queryFn: () => machinesApi.list({
      plant_id: plantId, department_id: departmentId,
      is_active: true, page: 1, page_size: 200,
    }),
    enabled: !!plantId,
  })
  const { data: employees } = useQuery({
    queryKey: ['emps', plantId, departmentId],
    queryFn: () => employeesApi.list({
      plant_id: plantId, department_id: departmentId,
      page: 1, size: 200,
    }),
    enabled: !!plantId,
  })

  const startMut = useMutation({
    mutationFn: () => liveStudyApi.startSession({
      template_id: templateId!,
      department_id: departmentId,
      machine_id: machineId,
      employee_id: employeeId,
      plant_id: plantId,
      // Phase 0090 — bind session to the originating To-Do task so that
      // completing the live capture auto-completes the task in the inbox.
      linked_task_id: taskId,
    }),
    onSuccess: (sess) => {
      message.success(`Session ${sess.session_no} opened`)
      navigate(`/hr/live-studies/sessions/${sess.id}`)
    },
    onError: (e: Error) => message.error(e.message),
  })

  const selectedTemplate = templates.find((t) => t.id === templateId)

  return (
    <>
      <PageHeader
        title="Start Study"
        subtitle="Pick template + context → start live capture"
        extra={<Button onClick={() => navigate('/hr/live-studies')}>Cancel</Button>}
      />
      {taskId && (
        <Alert
          type="success" showIcon style={{ marginBottom: 12 }}
          message="Driven from a To-Do task"
          description="Completing this live capture will auto-complete the originating To-Do in your inbox."
        />
      )}
      <Steps current={step} size="small" style={{ marginBottom: 24 }}
             items={[
               { title: 'Template' },
               { title: 'Context' },
               { title: 'Confirm' },
             ]} />

      {step === 0 && (
        <Card>
          <Form layout="vertical">
            <Form.Item label="Pick template">
              <Select
                size="large"
                placeholder="Select capture template"
                value={templateId}
                onChange={setTemplateId}
                options={templates.map((t) => ({
                  value: t.id,
                  label: `${t.template_name} (${t.capture_mode})`,
                }))}
              />
            </Form.Item>
            {selectedTemplate && (
              <Alert
                type="info" showIcon style={{ marginBottom: 12 }}
                message={selectedTemplate.template_name}
                description={
                  <Space wrap>
                    <Tag color="blue">{selectedTemplate.capture_mode}</Tag>
                    {selectedTemplate.default_duration_seconds && (
                      <Tag>{selectedTemplate.default_duration_seconds}s</Tag>
                    )}
                    {selectedTemplate.standard_value != null && (
                      <Tag>Standard: {selectedTemplate.standard_value} {selectedTemplate.standard_unit}</Tag>
                    )}
                    {selectedTemplate.capa_required_if_gap && <Tag color="orange">CAPA on gap</Tag>}
                  </Space>
                }
              />
            )}
            <Button
              type="primary"
              size="large"
              block
              disabled={!templateId}
              onClick={() => setStep(1)}
              style={{ height: 56, fontSize: 18 }}
            >
              Next: Pick Context →
            </Button>
          </Form>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <Form layout="vertical">
            <Form.Item label="Department">
              <Select
                size="large"
                allowClear showSearch
                placeholder="Pick department"
                value={departmentId}
                onChange={(v) => { setDepartmentId(v); setMachineId(undefined); setEmployeeId(undefined) }}
                options={(depts?.items ?? []).map((d) => ({ value: d.id, label: d.name }))}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item label="Machine">
              <Select
                size="large"
                allowClear showSearch
                placeholder="Pick machine"
                value={machineId}
                onChange={setMachineId}
                options={(machinesData?.items ?? []).map((m) => ({
                  value: m.id,
                  label: `${m.machine_id_str} · ${m.name}`,
                }))}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item label="Operator">
              <Select
                size="large"
                allowClear showSearch
                placeholder="Pick operator"
                value={employeeId}
                onChange={setEmployeeId}
                options={(employees?.items ?? []).map((e) => ({
                  value: e.id,
                  label: `${e.emp_name} (${e.emp_code})`,
                }))}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button size="large" onClick={() => setStep(0)}>← Back</Button>
              <Button
                type="primary"
                size="large"
                onClick={() => setStep(2)}
                style={{ minWidth: 200, height: 48 }}
              >
                Next: Confirm →
              </Button>
            </Space>
          </Form>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <Text strong style={{ fontSize: 16 }}>Confirm and start the session</Text>
          <div style={{ marginTop: 16 }}>
            <Space direction="vertical">
              <Text type="secondary">Template:</Text>
              <Text strong>{selectedTemplate?.template_name}</Text>
              {departmentId && (
                <>
                  <Text type="secondary">Department:</Text>
                  <Text strong>{(depts?.items || []).find((d) => d.id === departmentId)?.name}</Text>
                </>
              )}
              {machineId && (
                <>
                  <Text type="secondary">Machine:</Text>
                  <Text strong>{(machinesData?.items || []).find((m) => m.id === machineId)?.name}</Text>
                </>
              )}
              {employeeId && (
                <>
                  <Text type="secondary">Operator:</Text>
                  <Text strong>{(employees?.items || []).find((e) => e.id === employeeId)?.emp_name}</Text>
                </>
              )}
            </Space>
          </div>
          <Alert style={{ marginTop: 16 }} type="info" showIcon
                 message="ERP will auto-fetch skill level, certification status, supervisor and current allocation when the session opens." />
          <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 20 }}>
            <Button size="large" onClick={() => setStep(1)}>← Back</Button>
            <Button
              type="primary"
              size="large"
              onClick={() => startMut.mutate()}
              loading={startMut.isPending}
              disabled={!templateId}
              style={{ minWidth: 200, height: 56, fontSize: 18 }}
            >
              ▶ START SESSION
            </Button>
          </Space>
        </Card>
      )}
    </>
  )
}
